import { AGING_CLAIM_TARIFF_STATUSES } from '@billing/validation/tariff';
import React from 'react';
import { ClaimMetricBase, ProviderBreakdownItem } from './ClaimMetricBase';
import { useComputedColorScheme, useMantineTheme } from '@mantine/core';
import { useClaimPaginationStore } from 'store/ClaimPaginationStore';
import { nowISODate, parseDateTime, toISODate } from 'utils/dateUtils';
import { Status } from 'interfaces';
import { useCurrentSiteStore } from 'store';
import {
  ALL_CLAIM_TAB,
  useClaimTabFilterStore,
} from 'store/ClaimTabFilterStore';
import { nonTerminalClaimStatusEnumValues } from '@billing/validation/baseClaim';

interface AgingClaimsCardProps {
  total?: number;
  providers?: ProviderBreakdownItem[];
  loading: boolean;
  icon: React.ComponentType<any>;
  path?: string;
  navigate: (path: string) => void;
  statuses: Status[];
}

export const AgingClaimsCard: React.FC<AgingClaimsCardProps> = ({
  total,
  providers = [],
  loading,
  icon,
  navigate,
  path = '/claims',
}) => {
  const theme = useMantineTheme();
  const mode = useComputedColorScheme('dark');
  const badgeColour = theme.colors[`red-${mode}`][0];
  const { timezone } = useCurrentSiteStore();
  const { setColumnFilters, setPageIndex } = useClaimPaginationStore();
  const { clearSubStatuses, setActiveTab } = useClaimTabFilterStore();

  const applyFilter = (providerID: string) => {
    setActiveTab(ALL_CLAIM_TAB);
    clearSubStatuses();
    setPageIndex(0);
    setColumnFilters([]);
    const fourMonthsAgo = toISODate(
      parseDateTime(nowISODate(timezone), { zone: timezone }).minus({
        months: 4,
      })
    );
    const newFilter = [
      {
        id: 'tariffs.status',
        value: [...AGING_CLAIM_TARIFF_STATUSES],
      },
      {
        id: 'status',
        value: [...nonTerminalClaimStatusEnumValues],
      },
      {
        id: 'providerID',
        value: providerID,
      },
      {
        id: 'serviceStart',
        // Range: very early date to four months ago (inclusive) to capture only aging claims.
        value: ['1900-01-01', fourMonthsAgo],
      },
    ];
    setColumnFilters(newFilter);
    navigate('/claims');
  };

  return (
    <ClaimMetricBase
      title="Aging Claims"
      subtitle="120+ Days"
      total={total}
      providers={providers}
      loading={loading}
      badgeColour={badgeColour}
      onClick={applyFilter}
      icon={icon}
      path={path}
      headerLabel="Top Providers"
      emptyLabel="No stale claim providers"
      dataTestId="stale-claims-card"
    />
  );
};
